import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {
	type IMyAccountController,
	MyAccountController,
} from "views/pages/my_account/my_account.controller";
import {type ILoginController, LoginController} from "views/pages/login/login.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {CountriesStore, type ICountriesStore} from "data/stores/countries/countries.store";
import {RoundsStore, type IRoundsStore} from "data/stores/rounds/rounds.store";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squds.store";
import {
	type ISecretGateController,
	SecretGateController,
} from "views/controllers/secrete_gate/secret_gate.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/controllers/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {
	type IRegistrationController,
	RegistrationController,
} from "views/pages/registration/registration.controller";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	ForgotPasswordController,
	type IForgotPasswordController,
} from "views/pages/forgot_password/forgot_password.controller";
import {
	type IResetPasswordController,
	ResetPasswordController,
} from "views/pages/reset_password/reset_password.controller";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {GameController, type IGameController} from "views/pages/game/game.controller";
import {type IMenuController, MenuController} from "views/components/menu/menu.controller";
import {HeaderController, type IHeaderController} from "views/components/header/header.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {type IModalsController, ModalsController} from "views/controllers/modals/modals.controller";
import {
	CookieBannerController,
	type ICookieBannerController,
} from "views/components/CookieBanner/cookie_banner.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {type ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {type ITeamStore, TeamStore} from "data/stores/team/team.store";
import {type IPlayerPoolStore, PlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import {
	type IPoolController,
	PoolController,
} from "views/components/player_pool/pool/player_pool.controller";
import {
	type IPoolModalController,
	PoolModalController,
} from "views/components/player_pool/pool_modal/pool_modal.controller";
import {
	type IPlayerSlotController,
	PlayerSlotController,
} from "views/components/player_slot/slot/player_slot.controller";
import {
	GameBarController,
	type IGameBarController,
} from "views/components/gamebar/gamebar.controller";
import {
	type IStepperController,
	StepperController,
} from "views/components/lineup_stepper/lineup_stepper.controller";
import {type ITeamApiProvider, TeamApiProvider} from "data/providers/api/team.api.provider";
import {
	type ISavedPlayerSlotController,
	SavedPlayerSlotController,
} from "views/components/player_slot/saved_slot/saved_player_slot.controller";
import {
	type IPromptModalController,
	PromptModalController,
} from "views/components/modals/prompt_modal/prompt_modal.controller";
import {
	type IPlayerTooltipController,
	PlayerTooltipController,
} from "views/components/modals/player_tooltip/player_tooltip.controller";
import {type IShareStore, ShareStore} from "data/stores/share/share.store";
import {type IShareController, ShareController} from "views/controllers/share/share.controller";
import {
	GlobalModalsController,
	IGlobalModalsController,
} from "views/components/modals/global_modals.controller";
import {IStatesStore, StatesStore} from "data/stores/states/states.store";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<ITeamApiProvider>(Bindings.TeamApiProvider).to(TeamApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<ITeamStore>(Bindings.TeamStore).to(TeamStore).inSingletonScope();
	bind<IPlayerPoolStore>(Bindings.PlayerPoolStore).to(PlayerPoolStore).inSingletonScope();
	bind<IShareStore>(Bindings.ShareStore).to(ShareStore).inSingletonScope();
	bind<IStaticContentStore>(Bindings.StaticContentStore)
		.to(StaticContentStore)
		.inSingletonScope();

	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore);
	bind<IStatesStore>(Bindings.StatesStore).to(StatesStore);
	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore);
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<ILoginController>(Bindings.LoginController).to(LoginController);
	bind<IForgotPasswordController>(Bindings.ForgotPasswordController).to(ForgotPasswordController);
	bind<IResetPasswordController>(Bindings.ResetPasswordController).to(ResetPasswordController);
	bind<IRegistrationController>(Bindings.RegistrationController).to(RegistrationController);
	bind<IMyAccountController>(Bindings.MyAccountController).to(MyAccountController);
	bind<ISecretGateController>(Bindings.SecretGateController).to(SecretGateController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<IGameController>(Bindings.GameController).to(GameController);
	bind<IMenuController>(Bindings.MenuController).to(MenuController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IGlobalModalsController>(Bindings.GlobalModalsController).to(GlobalModalsController);
	bind<IModalsController>(Bindings.ModalsController).to(ModalsController);
	bind<ICookieBannerController>(Bindings.CookieBannerController).to(CookieBannerController);
	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<IPoolController>(Bindings.PoolController).to(PoolController);
	bind<IPoolModalController>(Bindings.PoolModalController).to(PoolModalController);
	bind<IPlayerSlotController>(Bindings.PlayerSlotController).to(PlayerSlotController);
	bind<IGameBarController>(Bindings.GameBarController).to(GameBarController);
	bind<IStepperController>(Bindings.StepperController).to(StepperController);
	bind<ISavedPlayerSlotController>(Bindings.SavedPlayerSlotController).to(
		SavedPlayerSlotController
	);
	bind<IPromptModalController>(Bindings.PromptModalController).to(PromptModalController);
	bind<IPlayerTooltipController>(Bindings.PlayerTooltipController).to(PlayerTooltipController);
	bind<IShareController>(Bindings.ShareController).to(ShareController);
});
